import { trpc } from '@tencent/tpro_rmonitor_metrics_server_get_metrics';
import { useLocalStorageState } from 'ahooks';
import { Form } from 'antd';
import { isPlainObject, unset } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import { getParamFromUrl, toSearchObj } from '@/infrastructure/history';
import {
  getProductId,
  isAndroid,
  isApple,
  isWindows,
  isElectron,
} from '@/infrastructure/product-info';
import { openXmMeter, openXmMeterOpenCes } from '@/infrastructure/reporter/xmMeter/index';
import { getCompareDateValue, isDataLabelingPlatform, judgmentTime } from '@/infrastructure/utils/issue';
import { CommonDateRange, DATE_TIME_FORMAT, getDateRangeByShortCut } from '@/infrastructure/utils/timestamp';
import { DenominatorTag } from '@/presentation/components/denominator-tag';
import {
  getAppInBack,
  getExceptionTypeOptions,
  getTableTimeQuickOptions,
  getTimeQuickOptions,
  TABLE_DEVICE_TYPE_OPTIONS,
} from '@/presentation/components/exception-form/utils';
import { FormEntry, FormType, IFormRef, QueryForm } from '@/presentation/components/form';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { CUSTOM, shortRanges } from '@/presentation/components/form/components/time-range-cascade';
import { FieldsOperator, FREE_DATE_RANGE, TIME_TYPE_OPTIONS_BUGLY } from '@/presentation/components/form/proto';

import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  INFERRED_LABELS_OPTIONS,
  MatchMode,
  NUMBER_MATCH_OPTIONS,
  SPECIFIC_TYPE,
  STACK_CHARACTER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import { iconText } from '@/presentation/components/hint-text/hintText';
import { ContinuousEventTips } from '@/presentation/components/issue-list/components/continuous-event-tips';
import {
  asanTypeTips,
  attachFileNamesText,
  cpuArchitectureText,
  cpuArchText,
  DATA_LABELING_TIPS,
  enumFilterTips,
  numberFilterTips,
  specialFilterTips,
  stringArrayFilterTips,
  versionFilterTips,
} from '@/presentation/components/issue-list/components/tips';
import useToggle from '@/presentation/components/toggle/use-toggle';
import { SEARCH_VALUES, TIME_RANGE } from '@/presentation/config/storage';
import { SearchValueStorageType, TimeRangeStorageType } from '@/presentation/config/type';
import { getIssueFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { useStores } from '@/presentation/store';
import {
  ANR_SCENE_DATA_LABELS_OPTIONS,
  CRASH_SCENE_DATA_LABELS_OPTIONS,
  ERROR_FOOM_SCENE_DATA_LABELS_OPTIONS,
  getMonitorType,
  INIT_FIELD_OPTIONS,
  IssueFilterOptions,
} from '@/presentation/store/issue';
import {
  ANDROID_ANR_LINKAGES_OPTION,
  ANDROID_ATTACH_FILE_OPTIONS,
  ANDROID_CRASH_LINKAGES_OPTION,
  ANDROID_FOOM_LINKAGES_OPTION,
  ASAN_TYPE_OPTION,
  ExOption,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  getLocalField,
  IOS_ATTACH_FILE_OPTIONS,
  Zone,
} from '@/proto/field';
import { getFieldZone, IssueType } from '@/proto/issue';
import { MetricType } from '@/proto/metric';
import { ProductType } from '@/service/product-permissions';
import { tokenizeValue } from '../form/utils';
import TimeType = trpc.rmonitor.metrics_server.TimeType;

// import styles from './index.module.scss';

export interface IProps {
  issueType: IssueType;
  metricType: MetricType;
  entry: FilterEntry;
  onFinish: (value: Record<string, any>) => void;
  initialValue?: any;
  onDeletedKeys?: (keys: string[]) => void;
  defaultValues?: any;
}

const dateRangeTransformMapping: Record<string, string | string[]> = {
  last_1_hour: '近1小时',
  last_1_day: '近1天',
  last_3_day: '近3天',
  last_7_day: '近7天',
  last_15_day: '近15天',
  last_30_day: '近30天',
};

export const getUrlFilterParams = () => {
  const query: Record<string, string | string[]> = toSearchObj(window.location.search);
  unset(query, 'productId');
  unset(query, 'pid');
  unset(query, 'appId');
  unset(query, 'token');

  Object.keys(query).forEach((queryKey: string) => {
    if (!query[queryKey] || query[queryKey] === 'all') {
      unset(query, queryKey);
    }
  });

  if (query.newTagList) {
    query.newTagList = [query.newTagList as string];
  }

  if (query.date && query.date !== 'custom') {
    query.customDate = dateRangeTransformMapping[query.date as string] as string;
  } else if (query.date && query.date === 'custom') {
    query.startDateStr = moment(query.startDateStr).format('YYYY-MM-DD 00:00:00');
    query.endDateStr = moment(query.endDateStr).format('YYYY-MM-DD 23:59:59');
    query.timeRange = [
      query.startDateStr,
      query.endDateStr,
    ];
  }

  return query;
};

export const ExceptionForm: FC<IProps> = observer((props) => {
  const { entry, metricType, issueType } = props;
  const [form] = Form.useForm();
  const [timeRangeState, setTimeRangeState] = useLocalStorageState<TimeRangeStorageType>(TIME_RANGE, {});
  const tableRef = useRef<IFormRef>(null);
  const [options, setOptions] = useState<IssueFilterOptions>(INIT_FIELD_OPTIONS);
  const [showDeviceType] = useToggle('deviceType', ['appId']);
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const { getOptions } = useStores().IssueStore;
  const getDefault = (name: string) => options[name]?.options?.[0]?.value;
  const exceptionTypeList = getExceptionTypeOptions(props.issueType) || [];
  const [mainStackEnabled] = useToggle('enableMainStack', ['appId']);
  const config = useAppConfig();
  const { filterConfig, isFreeProduct } = useStores().ProductStore;
  const isFree = isFreeProduct();
  const inferredLabelsConfig = getIssueFilterConfig(issueType, filterConfig);

  const isErrorList = entry === FilterEntry.ErrorList;
  const isBuglyListPage = [
    FilterEntry.ANRList,
    FilterEntry.CrashList,
    FilterEntry.ErrorList,
    FilterEntry.FoomList,
  ].includes(entry);
  const isBuglyDetailPage = [
    FilterEntry.ANRDetail,
    FilterEntry.CrashDetail,
    FilterEntry.ErrorDetail,
    FilterEntry.FoomDetail,
    FilterEntry.ANRAnalysis,
    FilterEntry.CrashAnalysis,
    FilterEntry.ErrorAnalysis,
    FilterEntry.FoomAnalysis,
  ].includes(entry);

  const isBuglyAnalysisPage = [
    FilterEntry.ANRAnalysis,
    FilterEntry.CrashAnalysis,
    FilterEntry.ErrorAnalysis,
    FilterEntry.FoomAnalysis,
  ].includes(entry);

  const isBuglyCaseDetailPage = [
    FilterEntry.ANRDetail,
    FilterEntry.CrashDetail,
    FilterEntry.ErrorDetail,
    FilterEntry.FoomDetail,
    FilterEntry.CrashCaller,
    FilterEntry.AndroidANRCaller,
  ].includes(entry);


  useEffect(() => {
    const load = async () => {
      const options = await getOptions(props.metricType);
      setOptions(options);
    };

    load();
  }, [props.metricType]);


  const judgmentTimeCallback = () => {
    form.setFieldsValue({
      timeQuick: CUSTOM,
      timeWindow: TimeType.DAY,
      timeRange: CommonDateRange.lastDaysFull(90),
    });
    form.submit();
  };

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    // 对时间值做动态更新
    if (value.timeQuick && value.timeQuick !== 'custom') {
      const timeRange = shortRanges?.[value.timeQuick!]?.() ?? [];
      if (timeRange[0] && timeRange[1]) {
        // eslint-disable-next-line no-param-reassign
        value.timeRange = [moment(timeRange[0]).format('YYYY-MM-DD HH:mm:ss'), moment(timeRange[1]).format('YYYY-MM-DD HH:mm:ss')];
      }
    }

    // 对比模式
    if (value.timeQuickCompare && value.timeQuickCompare !== 'custom') {
      const timeRange = shortRanges?.[value.timeQuickCompare!]?.() ?? [];
      if (timeRange[0] && timeRange[1]) {
        // eslint-disable-next-line no-param-reassign
        value.timeRangeCompare = [moment(timeRange[0]).format('YYYY-MM-DD HH:mm:ss'), moment(timeRange[1]).format('YYYY-MM-DD HH:mm:ss')];
      }
    }

    let expired = false;
    if (Array.isArray(value.timeRange) && value.timeRange.length === 2) {
      expired = judgmentTime(
        moment(value.timeRange[0]),
        moment(value.timeRange[1]),
        isBuglyListPage ? value.timeWindow : undefined,
        false,
        true,
        judgmentTimeCallback,
      );
    }
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    !expired && props.onFinish?.({ ...value, ...compareDateValue });

    // ces满意度弹窗
    if (compareSwitch) {
      openXmMeter('8a3e1wev7j6dy2lvmuco');
    }
    // 缓存时间
    if (value.timeWindow && value.timeQuick) {
      setTimeRangeState(state => ({
        ...state,
        [entry]: {
          timeWindow: value.timeWindow,
          timeQuick: value.timeQuick,
        },
      }));
    }
  };


  const searchValueDeserializer = (value?: SearchValueStorageType) => value?.map?.((item) => {
    const dateRange = getDateRangeByShortCut(item?.values?.timeQuick ?? '', shortRanges);
    if (!Array.isArray(dateRange) || dateRange?.length !== 2) {
      return item;
    }
    const compareDate: Record<string, any> = {};
    if (item?.values?.isCompareOpen) {
      const dateRangeCompare = getDateRangeByShortCut(item?.values?.timeQuickCompare ?? '', shortRanges);
      compareDate.startDateStrCompare = moment(dateRangeCompare[0]).format(DATE_TIME_FORMAT);
      compareDate.endDateStrCompare = moment(dateRangeCompare[1]).format(DATE_TIME_FORMAT);
    }
    const startDateStr = moment(dateRange[0]).format(DATE_TIME_FORMAT);
    const endDateStr = moment(dateRange[1]).format(DATE_TIME_FORMAT);
    return { name: item.name, id: item.id, values: { ...item?.values, startDateStr, endDateStr, ...compareDate } };
  }) || [];

  const getSceneDataLabelsOptions = () => {
    if ([FilterEntry.CrashDetail, FilterEntry.CrashCaller].includes(entry)) {
      return CRASH_SCENE_DATA_LABELS_OPTIONS;
    }
    if ([FilterEntry.ANRDetail, FilterEntry.AndroidANRCaller].includes(entry)) {
      return ANR_SCENE_DATA_LABELS_OPTIONS;
    }
    return ERROR_FOOM_SCENE_DATA_LABELS_OPTIONS;
  };

  const getLinkageOptions = () => {
    if (!isAndroid()) {
      return [];
    }
    switch (entry) {
      case FilterEntry.CrashList:
      case FilterEntry.CrashDetail:
      case FilterEntry.CrashCaller:
        return ANDROID_CRASH_LINKAGES_OPTION;
      case FilterEntry.ANRList:
      case FilterEntry.ANRDetail:
      case FilterEntry.AndroidANRCaller:
        return ANDROID_ANR_LINKAGES_OPTION;
      default:
        return [];
    }
  };

  const getAsanOptions = () => ASAN_TYPE_OPTION;

  const isLinkageDisabled = ![
    FilterEntry.CrashList,
    FilterEntry.CrashDetail,
    FilterEntry.CrashCaller,
    FilterEntry.ANRList,
    FilterEntry.ANRDetail,
    FilterEntry.AndroidANRCaller,
  ].includes(entry);

  // 是否展示小满费力度调查
  const isCesDisabled = [
    FilterEntry.CrashDetail,
    FilterEntry.CrashAnalysis,
  ].includes(entry);

  const getDefaultTime = useMemo(() => {
    const timeRangeStateData = timeRangeState?.[entry];
    // 有时间缓存则使用时间缓存，没有则使用默认时间
    if (timeRangeStateData?.timeQuick && timeRangeStateData.timeWindow) {
      // 是免费版但不是免费版的时间
      if (isFree && ![TimeType.HOUR, TimeType.DAY].includes(timeRangeStateData?.timeWindow)) {
        return { timeWindow: TimeType.HOUR,
          timeQuick: 'custom',
          initRange: CommonDateRange.today() };
      }
      let dateRange = getDateRangeByShortCut(timeRangeStateData?.timeQuick ?? '', shortRanges);
      if (timeRangeStateData?.timeQuick === CUSTOM
        && [TimeType.MINUTE5, TimeType.HOUR].includes(timeRangeStateData?.timeWindow)) {
        dateRange = getDateRangeByShortCut('今天', shortRanges);
      }
      if (timeRangeStateData?.timeQuick === CUSTOM
        && TimeType.WEEK === timeRangeStateData?.timeWindow) {
        dateRange = getDateRangeByShortCut('近30天', shortRanges);
      }
      if (timeRangeStateData?.timeQuick === CUSTOM
        && TimeType.MONTH === timeRangeStateData?.timeWindow) {
        dateRange = getDateRangeByShortCut('近90天', shortRanges);
      }
      return { timeQuick: timeRangeStateData?.timeQuick,
        timeWindow: timeRangeStateData?.timeWindow,
        initRange: dateRange };
    }
    return { timeWindow: isFree ? TimeType.HOUR : TimeType.MINUTE5,
      timeQuick: 'custom',
      initRange: CommonDateRange.today() };
  }, [timeRangeState]);

  const labelTools = isBuglyListPage ? [<DenominatorTag title={'支持联网下钻'}/>] : undefined;

  const getPlatformDisabledState = () => {
    if (isElectron() && entry === FilterEntry.CrashList) {
      return false;
    }

    if (isErrorList) {
      return false;
    }

    return true;
  };

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      labelTools,
      metricType: metricType === MetricType.TYPE_CRASH ? metricType : undefined,
      showTime: isBuglyCaseDetailPage || isBuglyListPage,
      timeQuickOptions: isBuglyListPage
        ? undefined : getTimeQuickOptions(props.issueType, isBuglyCaseDetailPage || isBuglyListPage),
      hiddenTimeWindow: !isBuglyListPage,
      isIssuesTimeWindow: isBuglyListPage,
      limitDays: isFree ? FREE_DATE_RANGE : DEFAULT_MONITOR_LIMIT_DAYS,
      initTimeWindow: getDefaultTime.timeWindow,
      initRange: getDefaultTime.initRange,
      defaultTimeQuick: getDefaultTime.timeQuick,
      timeTypeOptions: TIME_TYPE_OPTIONS_BUGLY,
      getTimeQuickOptions: isBuglyListPage
        ? timeWindow => (getTableTimeQuickOptions(
          props.issueType,
          isBuglyCaseDetailPage || isBuglyListPage, timeWindow,
        )) : undefined,
      formType: [ProductType.FREE_PRODUCT],
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: FieldKey.StandardAppVersion,
      label: FIELD_LABEL[FieldKey.AppVersion],
      isSplit: true,
      labelTools,
      fieldKey: FieldKey.AppVersion,
      fieldScope: getFieldZone(props.issueType),
      scope: 'issues',
      mode: 'tags',
      deleteable: false,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
        style: {
          width: 106,
        },
      },
      valueSelectProps: {
        mode: 'tags',
        style: {
          width: 450,
        },
        onBlur: isCesDisabled ? openXmMeterOpenCes : undefined,

      },
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.Select,
      name: 'exceptionTypeList',
      labelTools: [iconText(enumFilterTips)],
      label: 'Issue 类型',
      // deleteable: false,
      options: exceptionTypeList,
      mode: 'single',
      initialValue: exceptionTypeList?.[0]?.value,
      disabled: !isBuglyListPage
        || [IssueType.ISSUE_TYPE_ANR_BUGLY, IssueType.ISSUE_TYPE_FOOM_BUGLY].includes(props.issueType) || isElectron(),
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAccountId,
      label: '用户ID',
      isSplit: true,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.UserInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: isElectron() ? 'standardDeviceId' : FieldKey.StandardQimei,
      label: '设备ID',
      isSplit: true,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.NOT,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: 'standardStack',
      label: FIELD_LABEL[FieldKey.Stack],
      isSplit: true,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.TOKENIZATION,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.TOKENIZATION,
      },
      convertValue: tokenizeValue,
      modeOptions: STACK_CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'standardAppInBack',
      labelTools: [iconText(enumFilterTips)],
      label: '前后台状态',
      options: getLocalField(FieldKey.AppInBack)
        ?.map(val => ({ ...val, value: getAppInBack(val.value) })) as ExOption[],
      mode: 'multiple',
      initialValue: [getAppInBack(getDefault('appInBack'))],
      group: FormColumnGroup.ContextInfo,
      disabled: !(isBuglyListPage || isBuglyCaseDetailPage || isBuglyAnalysisPage) || isElectron(),
      initialDeleted: true,
    },
    {
      type: FormType.SelectNumberGroup,
      name: 'standardLaunchDuration',
      labelTools: [iconText(numberFilterTips)],
      label: '使用时长（秒）',
      options: NUMBER_MATCH_OPTIONS,
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: FieldsOperator.Lt,
          value: [value],
        };
      },
      disabled: !(isBuglyListPage || isBuglyCaseDetailPage),
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
    },
    {
      type: FormType.SelectNumberGroup,
      name: 'standardFreeMem',
      labelTools: [iconText(numberFilterTips)],
      label: '可用内存（GB）',
      options: NUMBER_MATCH_OPTIONS,
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: FieldsOperator.Lt,
          value: [value],
        };
      },
      compareDisabled: true,
      disabled: !(isBuglyListPage || isBuglyCaseDetailPage) || isElectron(),
      initialDeleted: true,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Select,
      name: 'deviceType',
      labelTools: [iconText(enumFilterTips)],
      label: '设备类型',
      options: TABLE_DEVICE_TYPE_OPTIONS,
      mode: 'multiple',
      initialValue: [TABLE_DEVICE_TYPE_OPTIONS?.[0]?.value],
      disabled: !showDeviceType,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'platform',
      labelTools: [iconText(enumFilterTips)],
      label: '平台',
      options: getLocalField(isElectron() ? FieldKey.ElectronPlatform : FieldKey.Platform) as ExOption[],
      mode: 'single',
      initialValue: FIELD_ALL,
      disabled: getPlatformDisabledState(),
      compareDisabled: getPlatformDisabledState(),
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: FieldKey.StandardBundleId,
      label: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      isSplit: true,
      labelTools,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: 'standardChannelId',
      label: '渠道',
      isSplit: true,
      fieldKey: FieldKey.ChannelId,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.Is,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      type: FormType.CharacterInput,
      name: 'standardStage',
      label: FIELD_LABEL[FieldKey.Stage],
      isSplit: true,
      fieldKey: FieldKey.Stage,
      fieldScope: getFieldZone(props.issueType),
      dropdownMatchSelectWidth: 500,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardBrand,
      label: FIELD_LABEL[FieldKey.Brand],
      isSplit: true,
      disabled: !isAndroid(),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardShiplyTag,
      labelTools: [iconText(stringArrayFilterTips), ...(labelTools || [])],
      label: FIELD_LABEL[FieldKey.ShiplyTag],
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.Exception,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      disabled: !config.enableShiplyAbTestTag || isElectron(),
      initialDeleted: true, // 默认隐藏
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAbTestTag,
      labelTools: [iconText(stringArrayFilterTips), ...(labelTools || [])],
      label: FIELD_LABEL[FieldKey.AbTestTag],
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.Exception,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      group: FormColumnGroup.Custom,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: FieldKey.StandardTagsInFilter,
      labelTools: [iconText(stringArrayFilterTips)],
      label: FIELD_LABEL[FieldKey.TagsInFilter],
      fieldScope: getMonitorType(metricType),
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      group: FormColumnGroup.Custom,
      disabled: isElectron(),
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardInferredLabels,
      labelTools: [iconText(DATA_LABELING_TIPS)],
      label: '数据标记',
      mode: 'tags',
      options: inferredLabelsConfig?.labelOptions || [],
      initialValue: {
        mode: MatchMode.NOT,
        values: inferredLabelsConfig?.values?.map(item => item.value),
      },
      modeSelectProps: {
        defaultValue: MatchMode.NOT,
      },
      modeOptions: INFERRED_LABELS_OPTIONS,
      disabled: !isDataLabelingPlatform(),
      group: FormColumnGroup.Tag,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardCpuArch',
      labelTools: [iconText(isApple() ? cpuArchitectureText : cpuArchText)],
      label: isApple() ? 'CPU架构' : '运行时架构',
      fieldScope: getFieldZone(props.issueType),
      fieldKey: FieldKey.CpuArc,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      initialDeleted: true, // 默认隐藏
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.DeviceInfo,
      disabled: isElectron(),
    },
    {
      type: FormType.CharacterInput,
      name: isElectron() ? 'standardElectronCpuArch' : 'standardCpuArchitecture',
      labelTools: [iconText(cpuArchitectureText)],
      label: 'CPU架构',
      fieldKey: isElectron() ? FieldKey.ElectronCpuArch : FieldKey.CpuArchitecture,
      fieldScope: getFieldZone(props.issueType),
      mode: 'single',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: isApple(),
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: FieldKey.StandardSdkVersion,
      labelTools: [iconText(versionFilterTips), ...(labelTools || [])],
      label: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
      dropdownMatchSelectWidth: 300,
      rangeProps: {
        dropDownWidth: 220,
      },
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: 'standardIssueId',
      label: 'Issue ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: isBuglyDetailPage,
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: isElectron() ? 'standardClientIdentify' : 'standardCrashId',
      label: '消息ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: 'standardErrorType',
      label: '异常名称',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: (props.issueType === IssueType.ISSUE_TYPE_FOOM_BUGLY) || isBuglyDetailPage || isElectron(),
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: 'standardHardware',
      label: '机型',
      mode: 'tags',
      fieldKey: FieldKey.Hardware,
      fieldScope: getFieldZone(props.issueType),
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: !isFree, // 默认隐藏
      group: FormColumnGroup.DeviceInfo,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: 'standardFrameworkVersion',
      label: '框架版本',
      labelString: '框架版本',
      mode: 'tags',
      fieldKey: FieldKey.FrameworkVersion,
      fieldScope: getFieldZone(props.issueType),
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
      disabled: !isElectron(),
    },
    {
      type: FormType.CharacterInput,
      name: 'standardNodeVersion',
      label: 'node版本',
      labelString: 'node版本',
      mode: 'tags',
      fieldKey: FieldKey.NodeVersion,
      fieldScope: getFieldZone(props.issueType),
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
      disabled: !isElectron(),
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardOsVersion,
      labelTools: [iconText(versionFilterTips)],
      label: FIELD_LABEL[FieldKey.OsVersion],
      fieldKey: FieldKey.OsVersion,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
      initialDeleted: !isFree, // 默认隐藏
      group: FormColumnGroup.DeviceInfo,
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: isElectron() ? 'standardProcessId' : 'standardProcessName',
      label: '出错进程',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      formType: [ProductType.FREE_PRODUCT],
      type: FormType.CharacterInput,
      name: 'standardThreadName',
      label: '出错线程',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      type: FormType.CharacterInput,
      name: 'standardMainStack',
      label: 'ANR Trace main线程堆栈',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: !mainStackEnabled || ![FilterEntry.ANRList, FilterEntry.ANRDetail].includes(entry),
      compareDisabled: true,
      initialDeleted: true, // 默认隐藏
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardRom',
      disabled: !((isBuglyListPage || isBuglyCaseDetailPage || isBuglyAnalysisPage) && isAndroid()),
      label: 'ROM',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      label: '异常现场',
      labelTools: [iconText('搜索包含以下现场信息的个例，选择多个时，只要包含其中一个即表示满足条件。')],
      type: FormType.CharacterInput,
      name: FieldKey.StandardSceneDataLabels,
      fieldKey: FieldKey.Model,
      options: getSceneDataLabelsOptions(),
      mode: 'multiple',
      disabled: isBuglyListPage || !isBuglyCaseDetailPage || !isAndroid(),
      compareDisabled: isBuglyListPage || !isBuglyCaseDetailPage || !isAndroid(),
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: SPECIFIC_TYPE,
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardLinkages',
      labelTools: [iconText(specialFilterTips)],
      label: '联动标记',
      options: getLinkageOptions(),
      mode: 'tags',
      disabled: isLinkageDisabled || !isAndroid(),
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: SPECIFIC_TYPE,
      group: FormColumnGroup.Tag,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.Linkages,
      label: '联动标记',
      options: ANDROID_FOOM_LINKAGES_OPTION,
      mode: 'tags',
      disabled:
        ![FilterEntry.FoomList, FilterEntry.FoomDetail].includes(props.entry),
      group: FormColumnGroup.Tag,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'oomType',
      labelTools: [iconText(enumFilterTips)],
      label: FIELD_LABEL[FieldKey.OOMType],
      fieldScope: Zone.AndroidFoom,
      fieldKey: FieldKey.OOMType,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
      initialDeleted: true,
      disabled: props.issueType !== IssueType.ISSUE_TYPE_FOOM_BUGLY || isBuglyDetailPage,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Select,
      name: FieldKey.StandardContinuousEvent,
      labelTools: [<ContinuousEventTips/>],
      label: '连续崩溃',
      fieldKey: FieldKey.ContinuousEvent,
      fieldScope: Zone.Exception,
      disabled: ![FilterEntry.CrashDetail, FilterEntry.CrashList].includes(props.entry) || isElectron(),
      mode: 'single',
      group: FormColumnGroup.Tag,
      initialDeleted: true,
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      fieldScope: getFieldZone(props.issueType),
      // initialDeleted: true,
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardArguments,
      label: '命令行参数',
      isSplit: true,
      mode: 'tags',
      group: FormColumnGroup.ApplicationInfo,
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
      initialDeleted: true,
      compareDisabled: true,
      disabled: !isWindows() || !(entry === FilterEntry.CrashList
        || entry === FilterEntry.CrashDetail),
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: '自定义维度',
      group: FormColumnGroup.Custom,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      type: FormType.BuglyCustomKeys,
      name: FieldKey.BizExtendInfo,
      label: '自定义字段',
      group: FormColumnGroup.Custom,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAttachFileNames,
      labelTools: [iconText(attachFileNamesText)],
      label: '附件',
      options: isApple()
        ? IOS_ATTACH_FILE_OPTIONS : ANDROID_ATTACH_FILE_OPTIONS,
      mode: 'tags',
      disabled: isBuglyListPage || !isBuglyCaseDetailPage,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: SPECIFIC_TYPE,
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardBuildNumber',
      label: '构建号',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
      disabled: isElectron(),
    },
    {
      type: FormType.Select,
      name: FieldKey.StandardAsanType,
      labelTools: [iconText(asanTypeTips)],
      label: FIELD_LABEL[FieldKey.AsanType],
      options: getAsanOptions(),
      fieldKey: FieldKey.AsanType,
      fieldScope: getFieldZone(props.issueType),
      mode: 'multiple',
      initialDeleted: true,
      disabled: !isAndroid() || (entry !== FilterEntry.CrashList && entry !== FilterEntry.CrashDetail),
      group: FormColumnGroup.exceptionInfo,
    },
  ];

  // 对于electron崩溃类型，过滤掉issue类型筛选项
  const filteredEntries = entries.filter((entry) => {
    // 如果是electron崩溃类型且是issue类型筛选项，则隐藏
    if (props.issueType === IssueType.ISSUE_TYPE_ELECTRON_CRASH && entry.name === 'exceptionTypeList') {
      return false;
    }
    return true;
  });

  useEffect(() => {
    const { initialValue } = props;
    if (initialValue) {
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(filteredEntries, initialValue)?.then?.(() => {
        form.setFieldsValue(initialValue);
        if (Array.isArray(initialValue.timeRange) && initialValue.timeRange.length === 2) {
          judgmentTime(
            moment(initialValue.timeRange[0]),
            moment(initialValue.timeRange[1]),
            isBuglyListPage ? initialValue.timeWindow : undefined,
            false,
            true,
            judgmentTimeCallback,
          );
        }
      });
    }
  }, [props.initialValue]);

  useEffect(() => {
    const token = getParamFromUrl('token');
    if (!token) {
      form.submit();
    }
  }, []);


  return <QueryForm
    formProps={{
      isKeyPressSubmit: false,
    }}
    ref={tableRef}
    form={form}
    entries={filteredEntries}
    onFinish={onFinish}
    pageEntry={props.entry}
    onDeletedKeys={props.onDeletedKeys}
    defaultValues={props.defaultValues}
    hasCompare={isBuglyListPage}
    issueType={props.issueType}
    cachePrefix={'prefix-12'}
    isSaveSearchValues={isBuglyListPage}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
    renderByGroup
  />;
});
